import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useRef } from 'react';

import { LightningGraphic1, LightningGraphic2 } from '../../components/svgs/LightningGraphic';
import { MemePointsPlacement } from '../../viewModels/gamification';
import type { IMeme } from '../../viewModels/gamification';

export type MemeOverlayProps = {
	meme: IMeme | null;
	/** Fires when the overlay auto-dismisses or the user clicks to dismiss. */
	onDismiss: () => void;
	/**
	 * Display duration in ms before auto-dismiss. Default 5000 — matches the
	 * legacy `GamificationViewModel.mMemeTimer` (`viewModels/gamification.ts:130`).
	 */
	durationMs?: number;
};

const DEFAULT_DURATION_MS = 5000;

const isMp4Meme = (meme: IMeme) => meme.image?.mimeType === 'video/mp4';

/**
 * Brief celebratory overlay shown after a successful Wrap-up save / Skip /
 * deal create. Renders the gamification meme image (or mp4) + phrase + points
 * pill positioned per `meme.theme.pointsPlacement`, with the legacy
 * `LightningGraphic` flair animating in the background and `meme.sound.url`
 * playback (when supplied).
 *
 * Auto-dismisses after `durationMs` (default 5s, matching legacy MemeModal)
 * or on click. The legacy queue routes meme display through MobX
 * `useGamification().setMeme(...)`; this surface keeps state local to the
 * focused-view WrapupPanel and skips the global store.
 *
 * Note on the C15 task framing: legacy memes are returned inline from the
 * dealForm/skipReason/phoneCall save HTTP responses — there is no separate
 * peer-event websocket push channel. The "server-pushed" routing fix is
 * therefore to ensure every save path that returns a meme threads it back
 * to this overlay (skip path is wired here; outcome and create-deal paths
 * already are via `WrapupPanel.handleSave` / `DealForm.onCreated`).
 */
export const MemeOverlay: FC<MemeOverlayProps> = ({ meme, onDismiss, durationMs = DEFAULT_DURATION_MS }) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);

	// Auto-dismiss timer.
	useEffect(() => {
		if (!meme) return;
		const handle = window.setTimeout(onDismiss, durationMs);
		return () => window.clearTimeout(handle);
	}, [meme, onDismiss, durationMs]);

	// Sound playback. Mirrors legacy MemeModal (`components/MemeModal/index.tsx:71-76`):
	// instantiate an Audio element on every meme change and play immediately.
	// We pause + null the ref on cleanup (overlay close OR a different meme)
	// so we don't leak two overlapping clips when a second save lands quickly.
	// `play()` returns a promise that rejects when autoplay policy blocks it
	// (no prior user gesture); swallow the rejection — the visual still works.
	useEffect(() => {
		const url = meme?.sound?.url;
		if (!url) return;
		const audio = new Audio(url);
		audioRef.current = audio;
		const playResult = audio.play();
		if (playResult && typeof playResult.catch === 'function') {
			playResult.catch(() => {
				/* autoplay blocked; visual overlay still renders */
			});
		}
		return () => {
			audio.pause();
			audioRef.current = null;
		};
	}, [meme]);

	if (!meme) return null;

	const handleDismiss = () => {
		audioRef.current?.pause();
		audioRef.current = null;
		onDismiss();
	};

	const renderMedia = () => {
		if (isMp4Meme(meme)) {
			// `muted` + `playsInline` are required for autoplay on mobile and on
			// most modern desktop browsers. `controls={false}` matches legacy.
			return (
				<video src={meme.image.url} className={css(styles.image)} autoPlay muted playsInline loop aria-label='Meme' />
			);
		}
		return <img src={meme.image.url} alt='Meme' className={css(styles.image)} />;
	};

	return (
		<div className={css(styles.root)} role='dialog' aria-label='Reward'>
			<LightningGraphic1
				className={css(styles.lightning1)}
				type={meme.memeType}
				primaryColor={meme.theme.primaryColor}
				secondaryColor={meme.theme.secondaryColor}
				backgroundColor={meme.theme.backgroundColor}
			/>
			<LightningGraphic2
				className={css(styles.lightning2)}
				type={meme.memeType}
				primaryColor={meme.theme.primaryColor}
				secondaryColor={meme.theme.secondaryColor}
				backgroundColor={meme.theme.backgroundColor}
			/>
			<div className={css(styles.card)} style={{ background: meme.theme.backgroundColor }} onClick={handleDismiss}>
				<div className={css(styles.imageWrap)}>
					{renderMedia()}
					{meme.value != null && (
						<span
							className={css(styles.points, placementToStyle(meme.theme.pointsPlacement))}
							style={{
								background: meme.theme.pointsBackgroundColor,
								color: meme.theme.pointsColor,
							}}
						>
							{meme.value}
						</span>
					)}
				</div>
				<p className={css(styles.phrase)} style={{ color: meme.theme.primaryColor }}>
					{meme.phrase}
				</p>
			</div>
		</div>
	);
};

function placementToStyle(p: MemePointsPlacement) {
	switch (p) {
		case MemePointsPlacement.Top:
			return styles.placementTop;
		case MemePointsPlacement.TopRight:
			return styles.placementTopRight;
		case MemePointsPlacement.Right:
			return styles.placementRight;
		case MemePointsPlacement.BottomRight:
			return styles.placementBottomRight;
		case MemePointsPlacement.Bottom:
			return styles.placementBottom;
		case MemePointsPlacement.BottomLeft:
			return styles.placementBottomLeft;
		case MemePointsPlacement.Left:
			return styles.placementLeft;
		case MemePointsPlacement.TopLeft:
			return styles.placementTopLeft;
		case MemePointsPlacement.Center:
		default:
			return styles.placementCenter;
	}
}

// Lightning sway keyframes mirror the legacy `MemeModal/styles.ts`
// `lightning1Keyframes` / `lightning2Keyframes` so the flair behaves
// identically. Aphrodite supports keyframe objects via `animationName`.
const lightning1Keyframes = {
	'0%': { top: -12, transform: 'rotateZ(10deg)' },
	'50%': { top: -12, transform: 'rotateZ(10deg)' },
	'51%': { top: -40, transform: 'rotateZ(-10deg)' },
	'100%': { top: -40, transform: 'rotateZ(-10deg)' },
};

const lightning2Keyframes = {
	'0%': { transform: 'rotateZ(10deg)' },
	'50%': { transform: 'rotateZ(10deg)' },
	'51%': { transform: 'rotateZ(-10deg)' },
	'100%': { transform: 'rotateZ(-10deg)' },
};

const styles = StyleSheet.create({
	root: {
		position: 'fixed',
		inset: 0,
		background: 'rgba(0, 0, 0, 0.55)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 100,
		overflow: 'hidden',
	},
	lightning1: {
		animationDuration: '1s',
		animationIterationCount: 'infinite',
		animationName: [lightning1Keyframes],
		left: 100,
		position: 'fixed',
		top: -12,
		pointerEvents: 'none',
	},
	lightning2: {
		animationDuration: '1s',
		animationIterationCount: 'infinite',
		animationName: [lightning2Keyframes],
		position: 'fixed',
		right: -60,
		top: '45vh',
		pointerEvents: 'none',
	},
	card: {
		position: 'relative',
		padding: 24,
		borderRadius: 16,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 12,
		cursor: 'pointer',
		maxWidth: 'min(90vw, 480px)',
	},
	imageWrap: {
		position: 'relative',
		width: 'min(70vw, 360px)',
		aspectRatio: '1 / 1',
	},
	image: {
		width: '100%',
		height: '100%',
		objectFit: 'contain',
	},
	points: {
		position: 'absolute',
		minWidth: 48,
		height: 48,
		borderRadius: 999,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 18,
		fontWeight: 700,
		padding: '0 12px',
		boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
	},
	placementCenter: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
	placementTop: { top: 0, left: '50%', transform: 'translate(-50%, -50%)' },
	placementBottom: { bottom: 0, left: '50%', transform: 'translate(-50%, 50%)' },
	placementLeft: { top: '50%', left: 0, transform: 'translate(-50%, -50%)' },
	placementRight: { top: '50%', right: 0, transform: 'translate(50%, -50%)' },
	placementTopLeft: { top: 0, left: 0, transform: 'translate(-25%, -25%)' },
	placementTopRight: { top: 0, right: 0, transform: 'translate(25%, -25%)' },
	placementBottomLeft: { bottom: 0, left: 0, transform: 'translate(-25%, 25%)' },
	placementBottomRight: { bottom: 0, right: 0, transform: 'translate(25%, 25%)' },
	phrase: {
		margin: 0,
		fontSize: 18,
		fontWeight: 600,
		textAlign: 'center',
	},
});
